import _ from 'lodash';
import { CORE_PRODUCT_TYPES_LIST } from 'client/constants/productTypes';
import IfpAppService from 'web-core/src/ifp-app-service';
import UserService from 'web-core/src/user-service';
import EligibilityService from 'web-core/src/ede/eligibility-service';
import EnrollmentService from 'web-core/src/ede/enrollment-service';

export const RECEIVE_IFP_APPS = 'RECEIVE_IFP_APPS';
export const REPLACE_IFP_APPS = 'REPLACE_IFP_APPS';
export const UPDATE_IFP_APP = 'UPDATE_IFP_APP';

export const receiveIfpApps = ifpApps => ({
  type: RECEIVE_IFP_APPS,
  ifpApps,
});

export const replaceIfpApps = ifpApps => ({
  type: REPLACE_IFP_APPS,
  ifpApps,
});

export const fetchOrCreateAppByType = (type, options = {}) => (
  dispatch,
  getState,
  { apiClient },
) => {
  const svc = new IfpAppService({ client: apiClient });
  return svc.query({ params: { type, limit: 1, ...options } }).then((response) => {
    // if the response is an array, it has hit the server with a logged in state
    if (Array.isArray(response)) {
      // if there's an app, return it
      if (response.length === 1) {
        dispatch(receiveIfpApps(response));
        return response;
      } else if (response.length > 1) {
        const latestApp = [_.head(response)];
        dispatch(receiveIfpApps(latestApp));
        return latestApp;
      }
      // if there's no incomplete app of this type, post one and return it
      return svc.post({ type }).then((app) => {
        dispatch(receiveIfpApps([app]));
        return app;
      });
    }

    // the server request failed due to user not logged in
    // create a new app of this type in the store
    if (_.get(response, 'message') === 'user not logged-in') {
      const app = {
        type,
        app_id: type,
      };
      dispatch(receiveIfpApps([app]));
      return app;
    }

    return null;
  });
};

export const fetchIfpApps = options => (dispatch, getState, { apiClient }) => {
  const svc = new IfpAppService({ client: apiClient });
  return svc.query(options).then((response) => {
    dispatch(receiveIfpApps(response));
    return response;
  });
};

export const fetchIfpAppIfNeeded = (appId, force = false) => (
  dispatch,
  getState,
  { apiClient },
) => {
  const apps = getState().ifpApps;
  const match = apps.filter(app => app.app_id === appId);

  if (match[0] && !force) {
    return Promise.resolve(match[0]);
  }

  const svc = new IfpAppService({ client: apiClient });
  // TODO: respond to error in fetch
  return svc.get(appId).then((response) => {
    dispatch(receiveIfpApps([response]));
    return response;
  });
};

export const resetApp = options => (dispatch, getState, { apiClient }) => {
  const svc = new IfpAppService({ client: apiClient });
  return svc.reset(options.app_id).then((response) => {
    dispatch(receiveIfpApps([response]));
    return response;
  });
};

export const copyApp = appId => (dispatch, getState, { apiClient }) => {
  const svc = new IfpAppService({ client: apiClient });
  let newApp;

  return svc
    .copy(appId)
    .then((response) => {
      newApp = response;
      dispatch(receiveIfpApps([response]));
      if (response.type !== 'health') {
        return newApp;
      }

      const userSvc = new UserService({ client: apiClient });
      return userSvc.post({ workingAppId: newApp.app_id });
    })
    .then(() => newApp);
};

export const postPlan = (appId, planArguments) => (dispatch, getState, { apiClient }) => {
  const svc = new IfpAppService({ client: apiClient });

  return svc.postPlan(appId, planArguments).then((response) => {
    if (response.status !== 'ERROR') {
      dispatch(receiveIfpApps([response]));
    }
    return response;
  });
};

export const saveMembers = (members, appId) => (dispatch, getState, { apiClient }) => {
  const svc = new IfpAppService({ client: apiClient });

  return svc.saveMembers(members, { app_id: appId }).then((response) => {
    dispatch(receiveIfpApps([response]));
    return response;
  });
};

export const updateApp = (appId, data) => ({
  type: UPDATE_IFP_APP,
  appId,
  data,
});

export const putApp = (appId, data) => (dispatch, getState, { apiClient }) => {
  const svc = new IfpAppService({ client: apiClient });

  return svc
    .put(appId, data)
    .then((response) => {
      dispatch(updateApp(appId, response));
      return response;
    })
    .catch(err => err);
};

export const eligibilitySign = (appId, data) => (dispatch, getState, { apiClient }) => {
  const svc = new EligibilityService({ client: apiClient });
  return svc
    .postEligibility(appId, data)
    .then(response => response)
    .catch(err => err);
};

export const enrollmentSign = appId => (dispatch, getState, { apiClient }) => {
  const svc = new EnrollmentService({ client: apiClient });
  return svc
    .post(appId)
    .then(response => response)
    .catch(err => err);
};

export const fetchActiveApps = () => (dispatch, getState, { apiClient }) => {
  const options = {
    types: CORE_PRODUCT_TYPES_LIST,
  };
  const svc = new IfpAppService({ client: apiClient });
  return svc
    .getActiveApps(options)
    .then((response) => {
      dispatch(replaceIfpApps(response));
      return response;
    })
    .catch(err => err);
};
